/**
 * The player's audio bus (ARCHITECTURE.md §8.4, BUILD-OVERRIDES.md).
 *
 *     AudioContext → voice (1.0) ┐
 *                    bgm   (0.6) ├→ master → DynamicsCompressor(−12, 3, 20) → destination
 *                    sfx   (0.9) ┘
 *
 * Responsibilities, and nothing else:
 *
 * - **Unlock on the first gesture** — Web Audio, the plate `<video>` and the
 *   screen wake lock all need that one tap (§8.4).
 * - **Play one narration line at a time**, from an offset, so a seek lands
 *   inside a line instead of restarting it.
 * - **Duck the music** under speech (0.6 → 0.18, back over ~0.3 s) with
 *   `setTargetAtTime`, never with a tween that owns state.
 * - **Never block on audio.** A cue with no baked `audio` (which is every
 *   cue in today's real bundles — `bundle.py` writes `"audio": null`) still
 *   plays: Web Speech when the browser has it, silence when it does not, and
 *   in both cases with estimated word timings so karaoke and the puppet's
 *   mouth have something honest to follow.
 * - A **missing sound degrades to silence** and never throws — the bucket has
 *   8 generic sfx plus 48 animal sounds (docs/ASSET-FACTS.md) and story text
 *   will ask for more than that.
 *
 * The bus owns no story time. `LineHandle.elapsed()` is offered as the
 * **audio-master clock** of §8.1 *only* when `handle.master` is true (a real
 * decoded buffer is playing); otherwise the runner keeps its own clamped
 * `StoryClock` and the rendering stays a pure function of it.
 *
 * **Playback speed** (`setRate`, 0.5×-2×, the control bar's menu). `elapsed()`
 * is always *seconds of the line* (content time), whatever the speed, so the
 * story clock that follows it and the karaoke index stay in step with the
 * words. A decoded line is sounded one of two ways:
 *
 * - at 1× by an `AudioBufferSourceNode` — sample-accurate, as before;
 * - at any other speed by one reused `<audio>` element with `preservesPitch`,
 *   routed through the same voice bus (so ducking, volume and the compressor
 *   still apply). A buffer source cannot change speed without changing pitch,
 *   and a chipmunk narrator at 1.5× is not a speed control. The element is
 *   unlocked inside the tap that starts the story or picks the speed (iOS),
 *   plays the bytes the bus already fetched (a blob URL) and, if it cannot
 *   play at all, the buffer takes over with `playbackRate` (pitch follows).
 *
 * A speed change mid-line re-times the line where it stands (the element in
 * place; the buffer hands over to the element at the same word).
 *
 * The buffer clock reads what is *heard*, not what is scheduled:
 * `getOutputTimestamp()` projected to now. `currentTime` runs one output
 * latency ahead (≈ 40 ms, far more on Bluetooth) and moves in the driver's
 * buffer steps, which put the mouths and the word highlight ahead of the
 * voice and made everything that follows the clock stutter.
 */

import { clampRate } from './clock'
import type { AudioPort, LineCue } from './index'
import { resolveAssetUrl } from './loader'
import type { LineWord } from './webspeech'
import { estimateLineDuration, estimateWords, WebSpeechProvider } from './webspeech'

/* ------------------------------------------------------------------ types */
/*
 * `AudioPort` and `PlayerUiState` belong to `player/index.ts` (core track).
 * This file implements `AudioPort` through `createAudioPort()` at the bottom;
 * `AudioBusPort` below is the bus's own, richer surface (preloading,
 * eviction, the wake lock, mute/volume) that the shell and the grown-up
 * panel use directly.
 */

/** The line the bus is speaking, as the subtitle band and the bubbles see it. */
export interface ActiveLine {
  /** Cue id. */
  id: string
  op: 'narrate' | 'say' | 'question'
  /** Speaking cast handle, or null for the narrator. */
  actor: string | null
  text: string
  words: LineWord[]
  dur: number
  timing: 'native' | 'estimated'
  /** Seconds since the line started — the runner's clamped local cue time. */
  tLocal: number
}

/** Resolved playable audio for one line. `url === null` → Web Speech or silence. */
export interface LineAudio {
  id: string
  text: string
  url: string | null
  dur: number
  words: LineWord[]
  timing: 'native' | 'estimated'
  /** Kokoro voice id, kept so the Web Speech fallback can pick a matching voice. */
  voice?: string | null
  /** Narration speed (§4.6.1 band table). */
  speed?: number
}

export interface PlayLineOptions {
  /** Seconds into the line to start at — a seek that lands mid-line (§8.1). */
  offset?: number
  onWord?: (index: number) => void
  onEnd?: (reason: 'end' | 'error' | 'cancel') => void
  /** Duck the music under this line. Default true. */
  duck?: boolean
}

export interface LineHandle {
  readonly id: string
  readonly dur: number
  readonly words: LineWord[]
  readonly timing: 'native' | 'estimated'
  readonly source: 'buffer' | 'speech' | 'silent'
  /**
   * True only for `source === 'buffer'`: the clock below is the decoded
   * stream's own clock, so §8.1's audio-master rule applies and karaoke
   * cannot drift. For speech/silence the runner keeps its own clock.
   */
  readonly master: boolean
  /** Seconds *of the line* heard so far (including `offset`), at any playback speed. */
  elapsed(): number
  stop(): void
}

export interface MusicOptions {
  gainDb?: number
  /** Cross-fade seconds. */
  fade?: number
  loop?: boolean
}

export interface SfxOptions {
  gainDb?: number
  duration?: number
  /** Seconds from now. Past-scheduled sfx are dropped (a seek must not replay them, ADDENDUM §5.2). */
  delay?: number
}

/**
 * The bus's own surface. `createAudioPort()` narrows it to the `AudioPort`
 * of `player/index.ts` that `runner.ts` consumes.
 */
export interface AudioBusPort {
  readonly unlocked: boolean
  unlock(): Promise<boolean>
  playLine(line: LineAudio, opts?: PlayLineOptions): LineHandle
  stopLine(): void
  setMusic(url: string | null, opts?: MusicOptions): void
  playSfx(url: string | null, opts?: SfxOptions): void
  stopSfx?(): void
  pauseBackground?(paused: boolean): void
  duck(on: boolean): void
  setMuted(muted: boolean): void
  setVolume(volume: number): void
  /** Playback speed, 0.5-2. Applies to the line playing (re-timed in place) and every line after it. */
  setRate(rate: number): void
  /** Older name of `setRate` (the panel's "voice speed"); one speed, never two multiplied. */
  setVoiceSpeed(rate: number): void
  preload(urls: readonly string[]): Promise<void>
  evict(keep: readonly string[]): void
  suspend(): void
  resume(): void
  acquireWakeLock(): Promise<boolean>
  releaseWakeLock(): void
  dispose(): void
}

/* -------------------------------------------------------------- constants */

/** §8.4 bus gains. */
export const BUS_GAIN = { voice: 1.0, bgm: 0.6, sfx: 0.9 } as const
/** §8.4 ducking: bgm 0.6 → 0.18 under speech, recovering after 0.3 s. */
export const DUCK_GAIN = 0.18
export const DUCK_ATTACK_TAU = 0.08
export const DUCK_RELEASE_DELAY_S = 0.3
export const DUCK_RELEASE_TAU = 0.1
/** §8.4 compressor. */
export const COMPRESSOR = { threshold: -12, ratio: 3, knee: 20 } as const
/** A voice element polled this long (ms) without its media time moving has stalled: the buffer takes over. */
export const VOICE_STALL_MS = 1500
/** Polls further apart than this (ms) — a hold, a hidden tab — restart the stall count. */
export const VOICE_POLL_GAP_MS = 250
/** A voice that could not be fetched or decoded is tried again after this long. */
export const FAILED_RETRY_MS = 15000

export function dbToGain(db: number): number {
  if (!Number.isFinite(db)) return 1
  return 10 ** (db / 20)
}

/* -------------------------------------------------- line resolution (pure) */

interface RawWord {
  w?: unknown
  s?: unknown
  e?: unknown
  t0?: unknown
  t1?: unknown
  ci?: unknown
  cl?: unknown
}

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback)

/**
 * Accept both word shapes seen in the stack: the schema's `{w, s, e, ci, cl}`
 * (`frontend/src/schema/story.ts#Word`, ARCHITECTURE §4.6.3) and the
 * `{w, t0, t1}` shorthand. Out-of-order or negative spans are repaired so
 * `wordIndexAt` stays monotone, which is what makes a seek land on the same
 * word as playing through.
 */
export function normalizeWords(words: readonly unknown[] | null | undefined, text: string): LineWord[] {
  if (!Array.isArray(words) || words.length === 0) return []
  const out: LineWord[] = []
  let prevEnd = 0
  let cursor = 0
  for (const raw of words as RawWord[]) {
    if (!raw || typeof raw !== 'object') continue
    const w = typeof raw.w === 'string' ? raw.w : ''
    if (!w) continue
    const s = Math.max(0, num(raw.s ?? raw.t0, prevEnd))
    const e = Math.max(s, num(raw.e ?? raw.t1, s))
    let ci = num(raw.ci, -1)
    if (ci < 0) {
      const at = text.indexOf(w, cursor)
      ci = at < 0 ? cursor : at
    }
    const cl = num(raw.cl, w.length) || w.length
    cursor = ci + cl
    prevEnd = e
    out.push({ w, s, e, ci, cl })
  }
  return out
}

/** The `audio` object of a bundle cue, as loosely as the wire may deliver it. */
export interface CueAudioLike {
  url?: unknown
  dur?: unknown
  words?: unknown
  timing?: unknown
}

export interface LineCueLike {
  id?: unknown
  op?: unknown
  actor?: unknown
  text?: unknown
  /** `question` cues use `prompt`. */
  prompt?: unknown
  dur?: unknown
  audio?: CueAudioLike | null
}

export interface ResolveLineOptions {
  /** The bundle's own directory — every `assets[].url` is relative to it (§4.6.4). */
  baseUrl?: string | URL
  /** Narration speed for the estimate (§4.6.1). Default 0.85. */
  speed?: number
  voice?: string | null
}

/**
 * Turn a bundle cue into something playable, baked or not.
 *
 * - baked audio with word timings  → `timing: 'native'`, played as a buffer.
 * - baked audio without timings    → proportional words over the real `dur`.
 * - **no audio at all**            → `url: null`, `dur` from the cue (the
 *   baker's estimate) or from `words / 2.1 + 0.4`, words estimated.
 *   `BUILD-OVERRIDES.md`: this is a supported way to play a story, not an error.
 */
export function resolveLine(cue: LineCueLike, opts: ResolveLineOptions = {}): LineAudio {
  const text = typeof cue.text === 'string' ? cue.text : typeof cue.prompt === 'string' ? cue.prompt : ''
  const id = typeof cue.id === 'string' ? cue.id : ''
  const speed = opts.speed ?? 0.85
  const audio = cue.audio && typeof cue.audio === 'object' ? cue.audio : null
  const rawUrl = audio && typeof audio.url === 'string' && audio.url.length > 0 ? audio.url : null
  const url = rawUrl ? resolveAssetUrl(rawUrl, opts.baseUrl) : null

  if (url) {
    const dur = num(audio?.dur, 0) || num(cue.dur, 0) || estimateLineDuration(text, speed)
    const words = normalizeWords(audio?.words as unknown[] | undefined, text)
    if (words.length > 0 && audio?.timing !== 'estimated') {
      return { id, text, url, dur, words, timing: 'native', voice: opts.voice ?? null, speed }
    }
    return { id, text, url, dur, words: words.length > 0 ? words : estimateWords(text, dur), timing: 'estimated', voice: opts.voice ?? null, speed }
  }

  const dur = num(cue.dur, 0) || estimateLineDuration(text, speed)
  return { id, text, url: null, dur, words: estimateWords(text, dur), timing: 'estimated', voice: opts.voice ?? null, speed }
}

/** Re-exported so `audio.ts` consumers need not know where it lives (one impl, `loader.ts`). */
export { resolveAssetUrl }

/* ----------------------------------------------------------------- the bus */

type ContextFactory = () => AudioContext

export interface AudioBusOptions {
  /** Injected in tests; in the browser this is `new AudioContext()`. */
  contextFactory?: ContextFactory
  /** `null` disables the Web Speech fallback (lines then play silently). */
  speech?: WebSpeechProvider | null
  /** Injected in tests. Defaults to `fetch` + `arrayBuffer()`. */
  fetchBytes?: (url: string) => Promise<ArrayBuffer>
  /** Injected in tests. Defaults to `new Audio()` for the streaming music bus. */
  audioElementFactory?: () => HTMLAudioElement
  /** Injected in tests. The one `<audio>` that voices lines at a speed other than 1×. Defaults to `audioElementFactory`. */
  voiceElementFactory?: () => HTMLAudioElement
  /** Keep the screen awake while a story plays (§8.4). Default true. */
  wakeLock?: boolean
  /** Max decoded line/sfx buffers kept resident. Older ones are evicted (§8.4). */
  cacheLimit?: number
}

interface Buses {
  ctx: AudioContext
  master: GainNode
  voice: GainNode
  bgm: GainNode
  sfx: GainNode
}

function defaultContextFactory(): AudioContext {
  const g = globalThis as unknown as { AudioContext?: new () => AudioContext; webkitAudioContext?: new () => AudioContext }
  const Ctor = g.AudioContext ?? g.webkitAudioContext
  if (!Ctor) throw new Error('web audio unavailable')
  return new Ctor()
}

function rampTo(param: AudioParam | undefined, value: number, when: number, tau: number): void {
  if (!param) return
  try {
    if (typeof param.setTargetAtTime === 'function') param.setTargetAtTime(value, when, tau)
    else param.value = value
  } catch {
    try {
      param.value = value
    } catch {
      /* ignore */
    }
  }
}

const noopHandle = (line: LineAudio, source: 'silent'): LineHandle => ({
  id: line.id,
  dur: line.dur,
  words: line.words,
  timing: line.timing,
  source,
  master: false,
  elapsed: () => 0,
  stop: () => {},
})

/**
 * One way of sounding a decoded line. `startDecoded`'s handle swaps these when
 * the speed changes or the element path fails, so the handle the port and the
 * clock hold stays the same object for the whole line.
 */
interface VoiceImpl {
  /** Seconds of the line heard so far (content time, rate-independent). */
  elapsed(): number
  /** Stop the sound without ending the line (no `onEnd`). */
  halt(): void
  /** Change speed in place; `false` = this path cannot, start another one at `elapsed()`. */
  setRate(rate: number): boolean
}

interface LiveLine {
  handle: LineHandle
  stop: (reason: 'end' | 'error' | 'cancel') => void
  /** The playback speed changed while this line plays. */
  retime: (rate: number) => void
}

interface VoiceElement {
  el: HTMLAudioElement
  node: MediaElementAudioSourceNode | null
  /** The src we last set (the element normalises `el.src`). */
  src: string
  /** Played once inside a gesture, which is what iOS asks of every media element. */
  primed: boolean
}

type PitchKeeping = HTMLAudioElement & { preservesPitch?: boolean; webkitPreservesPitch?: boolean; mozPreservesPitch?: boolean }

/** Speed with the voice's own pitch. `load()` resets `playbackRate`, so the default is set too. */
function setElementRate(el: HTMLAudioElement, rate: number): void {
  const m = el as PitchKeeping
  try {
    m.preservesPitch = true
    m.webkitPreservesPitch = true
    m.mozPreservesPitch = true
  } catch {
    /* an engine without the switch: the speed still changes */
  }
  try {
    el.defaultPlaybackRate = rate
    el.playbackRate = rate
  } catch {
    /* a stub element */
  }
}

let silentWav: string | null = null
/** A 1-sample silent WAV: what the voice element plays inside the unlocking tap (iOS). */
function silentWavUri(): string {
  if (silentWav) return silentWav
  const bytes = new Uint8Array(46)
  const view = new DataView(bytes.buffer)
  const ascii = (at: number, s: string): void => {
    for (let i = 0; i < s.length; i += 1) bytes[at + i] = s.charCodeAt(i)
  }
  ascii(0, 'RIFF')
  view.setUint32(4, 38, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, 8000, true)
  view.setUint32(28, 16000, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  ascii(36, 'data')
  view.setUint32(40, 2, true)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  silentWav = `data:audio/wav;base64,${typeof btoa === 'function' ? btoa(bin) : ''}`
  return silentWav
}

export class AudioBus implements AudioBusPort {
  private soundSources = new Set<AudioBufferSourceNode>()
  private retiringMusic = new Set<() => void>()
  private backgroundPaused = false
  private buses: Buses | null = null
  private readonly options: AudioBusOptions
  private readonly speech: WebSpeechProvider | null
  private readonly buffers = new Map<string, AudioBuffer>()
  /** The encoded bytes of each decoded line, for the voice element (decoding detaches the originals). */
  private readonly raw = new Map<string, ArrayBuffer>()
  private readonly blobUrls = new Map<string, string>()
  private readonly pending = new Map<string, Promise<AudioBuffer | null>>()
  private readonly failed = new Set<string>()
  private line: LiveLine | null = null
  private music: { url: string; el: HTMLAudioElement; node: MediaElementAudioSourceNode | null; gain: GainNode | null } | null = null
  private voiceEl: VoiceElement | null = null
  /** The element path failed once (no MediaElementSource, play() refused, a decode error): buffers only. */
  private voiceElBroken = false
  /** Bumped on every use of the voice element, so a late callback of an older use is ignored. */
  private voiceElToken = 0
  private ducked = false
  private muted = false
  private volume = 1
  /** Playback speed. 1 = the speed the line was baked/estimated at. */
  private rate = 1
  private wakeLockSentinel: { release: () => Promise<void>; addEventListener?: (t: string, cb: () => void) => void } | null = null
  private disposed = false

  constructor(options: AudioBusOptions = {}) {
    this.options = options
    this.speech = options.speech === undefined ? new WebSpeechProvider() : options.speech
  }

  get unlocked(): boolean {
    return this.buses !== null && this.buses.ctx.state !== 'suspended'
  }

  /** The live context, for a caller that must schedule against it. */
  get context(): AudioContext | null {
    return this.buses?.ctx ?? null
  }

  /**
   * One tap unlocks everything (§8.4): create/resume the context, play a
   * silent buffer (iOS only really unlocks on a started source), warm the
   * speech engine and take the wake lock. Returns false when the platform
   * has no Web Audio at all — the story still plays, silently.
   */
  async unlock(): Promise<boolean> {
    if (this.disposed) return false
    this.speech?.warmup()
    const buses = this.ensureBuses()
    if (!buses) {
      void this.acquireWakeLock()
      return false
    }
    // A story that opens at a remembered speed voices through the element, and
    // iOS lets an element play only after it played inside a gesture — this one.
    if (this.rate !== 1) this.primeVoiceElement()
    try {
      if (buses.ctx.state === 'suspended') await buses.ctx.resume()
    } catch {
      /* ignore */
    }
    try {
      const src = buses.ctx.createBufferSource()
      src.buffer = buses.ctx.createBuffer(1, 1, Math.max(8000, buses.ctx.sampleRate || 44100))
      src.connect(buses.master)
      src.start(0)
    } catch {
      /* ignore */
    }
    void this.acquireWakeLock()
    return this.unlocked
  }

  private ensureBuses(): Buses | null {
    if (this.buses) return this.buses
    if (this.disposed) return null
    try {
      const ctx = (this.options.contextFactory ?? defaultContextFactory)()
      const master = ctx.createGain()
      const compressor = ctx.createDynamicsCompressor()
      try {
        compressor.threshold.value = COMPRESSOR.threshold
        compressor.ratio.value = COMPRESSOR.ratio
        compressor.knee.value = COMPRESSOR.knee
      } catch {
        /* a stub compressor without params is fine */
      }
      const voice = ctx.createGain()
      const bgm = ctx.createGain()
      const sfx = ctx.createGain()
      voice.gain.value = BUS_GAIN.voice
      bgm.gain.value = BUS_GAIN.bgm
      sfx.gain.value = BUS_GAIN.sfx
      master.gain.value = this.muted ? 0 : this.volume
      voice.connect(master)
      bgm.connect(master)
      sfx.connect(master)
      master.connect(compressor)
      compressor.connect(ctx.destination)
      this.buses = { ctx, master, voice, bgm, sfx }
      return this.buses
    } catch {
      return null
    }
  }

  /* ------------------------------------------------------------ narration */

  playLine(line: LineAudio, opts: PlayLineOptions = {}): LineHandle {
    this.stopLine()
    const offset = Math.max(0, Math.min(opts.offset ?? 0, Math.max(0, line.dur - 0.01)))
    const wantDuck = opts.duck !== false
    if (wantDuck) this.duck(true)

    const finish = (reason: 'end' | 'error' | 'cancel'): void => {
      if (wantDuck) this.duck(false)
      opts.onEnd?.(reason)
    }

    const buffered = line.url ? this.buffers.get(line.url) : undefined
    const buses = this.ensureBuses()
    if (buffered && buses) {
      const handle = this.startDecoded(line, buffered, offset, buses, finish)
      if (handle) return handle
    }

    if (line.url && buses && !this.failed.has(line.url)) {
      // Not decoded yet (a slow first part, a seek, an evicted buffer): warm the buffer and
      // stream the real recording meanwhile. The reading voice (Web Speech) used to say it
      // here, and children heard a robot in the middle of a Fish-voiced story.
      void this.loadBuffer(line.url)
      const streamed = this.startStreamed(line, offset, opts, buses, finish)
      if (streamed) return streamed
    }

    return this.startFallback(line, offset, opts, finish)
  }

  /**
   * The baked recording through the voice element while its buffer is still decoding. It masters
   * the clock like a decoded line (the story waits the moment it takes to start). If the element
   * fails, the buffer takes over where it stood when it is decoded by then; only a recording that
   * cannot be played at all hands the rest of the line to the reading voice.
   */
  private startStreamed(line: LineAudio, offset: number, opts: PlayLineOptions, buses: Buses, finish: (r: 'end' | 'error' | 'cancel') => void): LineHandle | null {
    const url = line.url
    if (!url || !this.voiceElementUsable()) return null
    let stopped = false
    let impl: VoiceImpl | null = null
    let handed: LineHandle | null = null
    const stop = (reason: 'end' | 'error' | 'cancel'): void => {
      if (stopped) return
      stopped = true
      impl?.halt()
      if (this.line?.handle === handle) this.line = null
      finish(reason)
    }
    const onEnded = (): void => stop('end')
    const onFailed = (at: number): void => {
      if (stopped) return
      this.voiceElBroken = true
      impl?.halt()
      const buffer = this.buffers.get(url)
      impl = buffer ? this.bufferVoice(buffer, at, this.rate, buses, onEnded) : null
      if (impl) return
      stopped = true
      if (this.line?.handle === handle) this.line = null
      handed = this.startFallback(line, at, opts, finish)
    }
    impl = this.elementVoice(url, offset, this.rate, onEnded, onFailed)
    if (!impl) return null
    const handle: LineHandle = {
      id: line.id,
      dur: line.dur,
      words: line.words,
      timing: line.timing,
      source: 'buffer',
      master: true,
      elapsed: () => (handed ? handed.elapsed() : impl ? impl.elapsed() : offset),
      stop: () => (handed ? handed.stop() : stop('cancel')),
    }
    const retime = (rate: number): void => {
      if (!stopped) impl?.setRate(rate)
    }
    this.line = { handle, stop, retime }
    return handle
  }

  /**
   * A decoded line: the buffer at 1×, the pitch-keeping element at any other
   * speed (see the header). The handle is one object for the whole line; a
   * speed change or an element failure swaps what is under it at the same
   * point of the line.
   */
  private startDecoded(line: LineAudio, buffer: AudioBuffer, offset: number, buses: Buses, finish: (r: 'end' | 'error' | 'cancel') => void): LineHandle | null {
    let stopped = false
    let impl: VoiceImpl | null = null
    const stop = (reason: 'end' | 'error' | 'cancel'): void => {
      if (stopped) return
      stopped = true
      impl?.halt()
      if (this.line?.handle === handle) this.line = null
      finish(reason)
    }
    const onEnded = (): void => stop('end')
    const viaBuffer = (at: number, rate: number): VoiceImpl | null => this.bufferVoice(buffer, at, rate, buses, onEnded)
    // The element could not play (refused, undecodable, cross-origin): the
    // buffer takes over where it stood, and later lines skip the element.
    const onElementFailed = (at: number): void => {
      if (stopped) return
      this.voiceElBroken = true
      impl?.halt()
      impl = viaBuffer(at, this.rate)
      if (!impl) stop('error')
    }
    const make = (at: number, rate: number): VoiceImpl | null => {
      if (rate !== 1 && line.url) {
        const el = this.elementVoice(line.url, at, rate, onEnded, onElementFailed)
        if (el) return el
      }
      return viaBuffer(at, rate)
    }
    impl = make(offset, this.rate)
    if (!impl) return null
    const handle: LineHandle = {
      id: line.id,
      dur: buffer.duration || line.dur,
      words: line.words,
      timing: line.timing,
      source: 'buffer',
      master: true,
      elapsed: () => (impl ? impl.elapsed() : offset),
      stop: () => stop('cancel'),
    }
    const retime = (rate: number): void => {
      if (stopped || !impl) return
      if (impl.setRate(rate)) return
      const at = impl.elapsed()
      impl.halt()
      impl = make(at, rate)
      if (!impl) stop('error')
    }
    this.line = { handle, stop, retime }
    return handle
  }

  /** What the listener hears now, in context seconds: the output timestamp projected to now. */
  private heardTime(ctx: AudioContext): number {
    const now = ctx.currentTime
    const get = (ctx as { getOutputTimestamp?: () => AudioTimestamp }).getOutputTimestamp
    if (typeof get !== 'function') return now
    try {
      const ts = get.call(ctx)
      const ct = ts?.contextTime
      const pt = ts?.performanceTime
      if (typeof ct !== 'number' || typeof pt !== 'number' || !(pt > 0) || !Number.isFinite(ct)) return now
      // Never past the scheduled time: a suspended context stops `currentTime`, the projection would not.
      return Math.min(now, Math.max(0, ct + (nowMs() - pt) / 1000))
    } catch {
      return now
    }
  }

  /** The 1× path (and the fallback when the element cannot play): pitch follows `playbackRate`. */
  private bufferVoice(buffer: AudioBuffer, offset: number, rate: number, buses: Buses, onEnded: () => void): VoiceImpl | null {
    try {
      const src = buses.ctx.createBufferSource()
      src.buffer = buffer
      src.connect(buses.voice)
      try {
        src.playbackRate.value = rate
      } catch {
        /* a stub source without playbackRate is fine */
      }
      let since = buses.ctx.currentTime
      let base = offset
      let r = rate
      src.start(0, offset)
      src.onended = (): void => onEnded()
      let halted = false
      return {
        // Heard seconds × rate = seconds *of the line*, which is what the
        // karaoke index and the story clock are measured in.
        elapsed: () => base + Math.max(0, this.heardTime(buses.ctx) - since) * r,
        halt: () => {
          if (halted) return
          halted = true
          try {
            src.onended = null
            src.stop()
          } catch {
            /* already stopped */
          }
          try {
            src.disconnect()
          } catch {
            /* ignore */
          }
        },
        setRate: (next) => {
          // Another speed wants the pitch-keeping element; 1× (or no element) stays here.
          if (next !== 1 && this.voiceElementUsable()) return false
          const heard = this.heardTime(buses.ctx)
          base += Math.max(0, heard - since) * r
          since = Math.max(since, heard)
          r = next
          try {
            src.playbackRate.value = next
          } catch {
            /* ignore */
          }
          return true
        },
      }
    } catch {
      return null
    }
  }

  private voiceElementUsable(): boolean {
    return !this.voiceElBroken && !this.disposed && this.ensureVoiceElement() !== null
  }

  private ensureVoiceElement(): VoiceElement | null {
    if (this.voiceEl) return this.voiceEl
    if (this.voiceElBroken || this.disposed) return null
    const buses = this.ensureBuses()
    if (!buses) return null
    try {
      const factory = this.options.voiceElementFactory ?? this.options.audioElementFactory ?? (() => new Audio())
      const el = factory()
      el.preload = 'auto'
      // Without CORS a cross-origin element feeds the graph silence; with it,
      // a server that refuses fails loudly and the buffer takes over.
      el.crossOrigin = 'anonymous'
      const node = buses.ctx.createMediaElementSource(el)
      node.connect(buses.voice)
      this.voiceEl = { el, node, src: '', primed: false }
      return this.voiceEl
    } catch {
      this.voiceElBroken = true
      return null
    }
  }

  /** Play the voice element once inside the current gesture (iOS). Idempotent. */
  private primeVoiceElement(): void {
    const v = this.ensureVoiceElement()
    if (!v || v.primed) return
    v.primed = true
    const silent = silentWavUri()
    const token = ++this.voiceElToken
    try {
      v.el.src = silent
      v.src = silent
      const started = v.el.play()
      if (started && typeof started.then === 'function') {
        started.then(
          () => {
            if (this.voiceElToken === token && v.src === silent) v.el.pause()
          },
          () => undefined,
        )
      }
    } catch {
      /* the first real line finds out */
    }
  }

  /** Encoded bytes as a blob URL when the bus has them (no second download), else the URL itself. */
  private playableUrl(url: string): string {
    const have = this.blobUrls.get(url)
    if (have) return have
    const bytes = this.raw.get(url)
    const U = globalThis.URL as typeof URL | undefined
    if (!bytes || typeof U?.createObjectURL !== 'function' || typeof Blob === 'undefined') return url
    try {
      const blobUrl = U.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }))
      this.blobUrls.set(url, blobUrl)
      return blobUrl
    } catch {
      return url
    }
  }

  /** Drop the kept bytes of `url` (never the blob the voice element is playing). */
  private forgetRaw(url: string): void {
    this.raw.delete(url)
    const blobUrl = this.blobUrls.get(url)
    if (!blobUrl || this.voiceEl?.src === blobUrl) return
    this.blobUrls.delete(url)
    try {
      globalThis.URL?.revokeObjectURL?.(blobUrl)
    } catch {
      /* ignore */
    }
  }

  /**
   * Any speed but 1×: the reused `<audio>`, pitch kept; `elapsed()` is its own media time.
   *
   * The story clock follows that time and waits for it, so an element that never moves (a play()
   * that stays pending, a source that never loads, an OS that paused it) would hold the story still
   * for good — the buffer path cannot stall like that. `elapsed()` is therefore also a watchdog:
   * polled for `VOICE_STALL_MS` without the media time moving, the element counts as failed and the
   * buffer takes over from where it stood. A gap between polls (a hold, a hidden tab) is not a stall.
   */
  private elementVoice(url: string, offset: number, rate: number, onEnded: () => void, onFailed: (at: number) => void): VoiceImpl | null {
    if (this.voiceElBroken) return null
    const v = this.ensureVoiceElement()
    if (!v) return null
    const el = v.el
    const token = ++this.voiceElToken
    let halted = false
    let last = offset
    let movedAt = nowMs()
    let polledAt = movedAt
    const live = (): boolean => !halted && this.voiceElToken === token
    try {
      el.onended = null
      el.onerror = null
      const src = this.playableUrl(url)
      if (v.src !== src) {
        el.src = src
        v.src = src
      }
      setElementRate(el, rate)
      el.currentTime = offset
      el.onended = (): void => {
        if (live()) onEnded()
      }
      el.onerror = (): void => {
        if (live()) onFailed(last)
      }
      const started = el.play()
      if (started && typeof started.catch === 'function') {
        started.catch(() => {
          if (live()) onFailed(last)
        })
      }
    } catch {
      return null
    }
    return {
      elapsed: () => {
        if (live()) {
          const now = nowMs()
          if (now - polledAt > VOICE_POLL_GAP_MS) movedAt = now
          polledAt = now
          // Before the first frame plays the element reports the start position: the story waits.
          const ct = el.currentTime
          if (Number.isFinite(ct) && ct >= 0 && ct !== last) {
            last = ct
            movedAt = now
          }
          if (now - movedAt > VOICE_STALL_MS && !el.ended) onFailed(last)
        }
        return last
      },
      halt: () => {
        if (halted) return
        halted = true
        if (this.voiceElToken === token) {
          el.onended = null
          el.onerror = null
          try {
            el.pause()
          } catch {
            /* ignore */
          }
        }
      },
      setRate: (next) => {
        if (live()) setElementRate(el, next)
        return true
      },
    }
  }

  private startFallback(line: LineAudio, offset: number, opts: PlayLineOptions, finish: (r: 'end' | 'error' | 'cancel') => void): LineHandle {
    let stopped = false
    let speakHandle: { cancel(): void } | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    // Line seconds = wall seconds × rate, re-based on every speed change.
    let base = offset
    let sinceMs = nowMs()
    let r = this.rate
    const elapsed = (): number => base + Math.max(0, (nowMs() - sinceMs) / 1000) * r

    const stop = (reason: 'end' | 'error' | 'cancel'): void => {
      if (stopped) return
      stopped = true
      if (timer !== null) clearTimeout(timer)
      timer = null
      if (reason === 'cancel') speakHandle?.cancel()
      speakHandle = null
      if (this.line?.handle === handle) this.line = null
      finish(reason)
    }
    // No voices at all: hold the line open for what is left of it at this speed
    // so the subtitle band and the timeline keep the pacing of a read story.
    const arm = (): void => {
      if (timer !== null) clearTimeout(timer)
      const left = Math.max(0, line.dur - elapsed())
      timer = setTimeout(() => stop('end'), Math.round((left * 1000) / r))
    }

    const handle: LineHandle = {
      id: line.id,
      dur: line.dur,
      words: line.words,
      timing: 'estimated',
      source: this.speech?.available ? 'speech' : 'silent',
      master: false,
      elapsed,
      stop: () => stop('cancel'),
    }
    // Web Speech takes a new rate from its next chunk (the provider has it); the silent timer is re-armed.
    const retime = (next: number): void => {
      if (stopped) return
      base = elapsed()
      sinceMs = nowMs()
      r = next
      if (timer !== null) arm()
    }
    this.line = { handle, stop, retime }

    if (this.speech?.available && line.text.trim().length > 0) {
      const spoken = offset > 0 ? sliceFromOffset(line, offset) : line.text
      speakHandle = this.speech.speak(spoken, {
        speed: line.speed ?? 0.85,
        lang: 'en-US',
        onWord: (index) => {
          if (!stopped) opts.onWord?.(index + wordsBefore(line, offset))
        },
        onEnd: (reason) => stop(reason === 'cancel' ? 'cancel' : 'end'),
      })
    } else {
      arm()
    }
    return handle
  }

  stopLine(): void {
    const cur = this.line
    this.line = null
    cur?.stop('cancel')
    this.speech?.cancel()
  }

  /** The line playing now, for `runner.ts`'s audio-master clock check. */
  get currentLine(): LineHandle | null {
    return this.line?.handle ?? null
  }

  /* ---------------------------------------------------------------- duck */

  duck(on: boolean): void {
    if (this.ducked === on) return
    this.ducked = on
    const buses = this.buses
    if (!buses) return
    const now = buses.ctx.currentTime
    if (on) rampTo(buses.bgm.gain, DUCK_GAIN, now, DUCK_ATTACK_TAU)
    else rampTo(buses.bgm.gain, BUS_GAIN.bgm, now + DUCK_RELEASE_DELAY_S, DUCK_RELEASE_TAU)
  }

  get isDucked(): boolean {
    return this.ducked
  }

  /* --------------------------------------------------------------- music */

  /**
   * Music streams through a `MediaElementAudioSourceNode` (§8.4) — the
   * background tracks are 0.25–2.4 MB (docs/ASSET-FACTS.md) and must not be
   * decoded into memory next to the clip textures.
   */
  setMusic(url: string | null, opts: MusicOptions = {}): void {
    if (this.music && this.music.url === url) {
      if (opts.gainDb !== undefined && this.music.gain) rampTo(this.music.gain.gain, dbToGain(opts.gainDb), this.buses?.ctx.currentTime ?? 0, Math.max(0.01, (opts.fade ?? 0.5) / 3))
      return
    }
    this.stopMusic(opts.fade ?? 0.5)
    if (!url) return
    const buses = this.ensureBuses()
    try {
      const el = (this.options.audioElementFactory ?? (() => new Audio()))()
      el.src = url
      el.loop = opts.loop !== false
      el.crossOrigin = 'anonymous'
      el.preload = 'auto'
      // A track the bucket does not have must be silence, never a thrown error.
      el.onerror = (): void => { if (this.music?.el === el) this.stopMusic(0) }
      let node: MediaElementAudioSourceNode | null = null
      let gain: GainNode | null = null
      if (buses) {
        node = buses.ctx.createMediaElementSource(el)
        gain = buses.ctx.createGain()
        gain.gain.setValueAtTime(0, buses.ctx.currentTime)
        gain.gain.linearRampToValueAtTime(dbToGain(opts.gainDb ?? -20), buses.ctx.currentTime + Math.max(.01, opts.fade ?? .5))
        node.connect(gain)
        gain.connect(buses.bgm)
      } else {
        el.volume = Math.min(1, dbToGain(opts.gainDb ?? 0) * BUS_GAIN.bgm)
      }
      this.music = { url, el, node, gain }
      const started = this.backgroundPaused ? undefined : el.play()
      if (started && typeof started.catch === 'function') started.catch(() => {})
    } catch {
      this.music = null
    }
  }

  private stopMusic(fade = 0): void {
    const cur = this.music
    this.music = null
    if (!cur) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const cleanup = (): void => {
      if (timer !== undefined) clearTimeout(timer)
      this.retiringMusic.delete(cleanup)
      try {
        cur.el.onerror = null
        cur.el.pause()
        cur.el.removeAttribute('src')
        cur.node?.disconnect()
        cur.gain?.disconnect()
      } catch { /* ignore */ }
    }
    if (fade > 0 && cur.gain && this.buses && !this.backgroundPaused) {
      const now = this.buses.ctx.currentTime
      cur.gain.gain.cancelScheduledValues(now)
      cur.gain.gain.setValueAtTime(cur.gain.gain.value, now)
      cur.gain.gain.linearRampToValueAtTime(0, now + fade)
      this.retiringMusic.add(cleanup)
      timer = setTimeout(cleanup, fade * 1000 + 20)
    } else cleanup()
  }

  pauseBackground(paused: boolean): void {
    this.backgroundPaused = paused
    if (paused) {
      this.stopSfx()
      for (const cleanup of this.retiringMusic) cleanup()
      this.music?.el.pause()
    } else {
      const started = this.music?.el.play()
      if (started && typeof started.catch === 'function') started.catch(() => {})
    }
  }

  /* ----------------------------------------------------------------- sfx */

  /** A sound the bucket does not carry is silence, by design (docs/ASSET-FACTS.md). */
  playSfx(url: string | null, opts: SfxOptions = {}): void {
    if (!url) return
    if ((opts.delay ?? 0) < 0) return // a seek must not replay past sfx (ADDENDUM §5.2)
    const buses = this.ensureBuses()
    if (!buses) return
    const buffer = this.buffers.get(url)
    if (!buffer) {
      void this.loadBuffer(url)
      return
    }
    try {
      const src = buses.ctx.createBufferSource()
      src.buffer = buffer
      const gain = buses.ctx.createGain()
      gain.gain.value = dbToGain(opts.gainDb ?? 0)
      src.connect(gain)
      gain.connect(buses.sfx)
      this.soundSources.add(src)
      src.start(buses.ctx.currentTime + (opts.delay ?? 0), 0, Math.max(.01, Math.min(buffer.duration, opts.duration ?? buffer.duration)))
      src.onended = (): void => {
        this.soundSources.delete(src)
        try {
          src.disconnect()
          gain.disconnect()
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* silence */
    }
  }

  /* -------------------------------------------------------------- buffers */

  stopSfx(): void {
    for (const source of this.soundSources) {
      try { source.stop() } catch { /* already ended */ }
    }
    this.soundSources.clear()
  }

  async preload(urls: readonly string[]): Promise<void> {
    await Promise.all(urls.filter(Boolean).map((u) => this.loadBuffer(u)))
  }

  /** Drop every decoded buffer except `keep` (scene N ∪ N+1, §8.5). */
  evict(keep: readonly string[]): void {
    const keepSet = new Set(keep)
    for (const url of [...this.buffers.keys()]) {
      if (keepSet.has(url)) continue
      this.buffers.delete(url)
      this.forgetRaw(url)
    }
  }

  private loadBuffer(url: string): Promise<AudioBuffer | null> {
    const have = this.buffers.get(url)
    if (have) return Promise.resolve(have)
    if (this.failed.has(url)) return Promise.resolve(null)
    const inflight = this.pending.get(url)
    if (inflight) return inflight
    const job = (async (): Promise<AudioBuffer | null> => {
      try {
        const buses = this.ensureBuses()
        if (!buses) return null
        const fetcher = this.options.fetchBytes ?? defaultFetchBytes
        const bytes = await fetcher(url)
        // `decodeAudioData` detaches its argument; the voice element needs the
        // encoded bytes for any speed but 1× (a few tens of KB per line).
        let copy: ArrayBuffer | null = null
        try {
          copy = bytes.slice(0)
        } catch {
          copy = null
        }
        const decoded = await buses.ctx.decodeAudioData(bytes)
        this.buffers.set(url, decoded)
        if (copy) this.raw.set(url, copy)
        const limit = this.options.cacheLimit ?? 64
        while (this.buffers.size > limit) {
          const oldest = this.buffers.keys().next()
          if (oldest.done) break
          this.buffers.delete(oldest.value)
          this.forgetRaw(oldest.value)
        }
        return decoded
      } catch {
        // 404, CORS, an mp3 this browser cannot decode: remember and move on — for a while only:
        // a blip (the server restarting) must not leave that line in the reading voice for good.
        this.failed.add(url)
        setTimeout(() => this.failed.delete(url), FAILED_RETRY_MS)
        return null
      } finally {
        this.pending.delete(url)
      }
    })()
    this.pending.set(url, job)
    return job
  }

  /** True once the line's bytes are decoded, so the runner can prefer the baked take. */
  hasBuffer(url: string | null): boolean {
    return url !== null && this.buffers.has(url)
  }

  /* --------------------------------------------------------- volume/state */

  setMuted(muted: boolean): void {
    this.muted = muted
    const buses = this.buses
    if (!buses) return
    rampTo(buses.master.gain, muted ? 0 : this.volume, buses.ctx.currentTime, 0.02)
  }

  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume))
    const buses = this.buses
    if (!buses || this.muted) return
    rampTo(buses.master.gain, this.volume, buses.ctx.currentTime, 0.02)
  }

  /**
   * Playback speed, 0.5-2 (the control bar's menu, `<` / `>`, the panel).
   *
   * One speed for everything the story says: a decoded line is re-timed where
   * it stands (the pitch-keeping element at any speed but 1×, see the header),
   * the Web Speech fallback gets it as `utterance.rate` from its next chunk,
   * and a silent line's timer is re-armed. The story clock gets the same
   * number from the player, so story seconds, cue times and karaoke never
   * change — the story just passes through them faster. Music stays at 1×.
   *
   * Called from the tap that picked the speed, which is also where iOS lets
   * the voice element be unlocked.
   */
  setRate(rate: number): void {
    const r = clampRate(rate)
    this.rate = r
    this.speech?.setVoiceSpeed(r)
    if (r !== 1 && this.buses) this.primeVoiceElement()
    this.line?.retime(r)
  }

  /** The panel's older name for `setRate`. */
  setVoiceSpeed(rate: number): void {
    this.setRate(rate)
  }

  /** The speed lines are playing at (0.5-2). */
  get voiceRate(): number {
    return this.rate
  }

  suspend(): void {
    this.stopLine()
    try {
      this.music?.el.pause()
    } catch {
      /* ignore */
    }
    try {
      void this.buses?.ctx.suspend()
    } catch {
      /* ignore */
    }
  }

  resume(): void {
    try {
      void this.buses?.ctx.resume()
    } catch {
      /* ignore */
    }
    try {
      const started = this.music?.el.play()
      if (started && typeof started.catch === 'function') started.catch(() => {})
    } catch {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------ wake lock */

  /** §8.4/§8.6: held while playing, released in `dimmed`, `paused` and `ended`. */
  async acquireWakeLock(): Promise<boolean> {
    if (this.options.wakeLock === false || this.wakeLockSentinel) return this.wakeLockSentinel !== null
    try {
      const nav = (globalThis as unknown as { navigator?: { wakeLock?: { request: (t: string) => Promise<unknown> } } }).navigator
      const sentinel = await nav?.wakeLock?.request('screen')
      if (!sentinel) return false
      this.wakeLockSentinel = sentinel as { release: () => Promise<void> }
      return true
    } catch {
      // iOS < 16.4, Firefox, a background tab: a story without a wake lock is
      // still a story.
      return false
    }
  }

  releaseWakeLock(): void {
    const sentinel = this.wakeLockSentinel
    this.wakeLockSentinel = null
    try {
      void sentinel?.release()
    } catch {
      /* ignore */
    }
  }

  dispose(): void {
    this.disposed = true
    this.stopSfx()
    this.stopLine()
    this.stopMusic(0)
    for (const cleanup of this.retiringMusic) cleanup()
    this.releaseWakeLock()
    const voice = this.voiceEl
    this.voiceEl = null
    if (voice) {
      try {
        voice.el.onended = null
        voice.el.onerror = null
        voice.el.pause()
        voice.el.removeAttribute('src')
        voice.node?.disconnect()
      } catch {
        /* ignore */
      }
    }
    for (const blobUrl of this.blobUrls.values()) {
      try {
        globalThis.URL?.revokeObjectURL?.(blobUrl)
      } catch {
        /* ignore */
      }
    }
    this.blobUrls.clear()
    this.raw.clear()
    this.buffers.clear()
    this.pending.clear()
    try {
      void this.buses?.ctx.close()
    } catch {
      /* ignore */
    }
    this.buses = null
  }
}

/* --------------------------------------------------------------- helpers */

function nowMs(): number {
  const perf = (globalThis as unknown as { performance?: { now(): number } }).performance
  return perf ? perf.now() : Date.now()
}

/** Words fully before `offset` — so a mid-line seek reports the right word index. */
export function wordsBefore(line: LineAudio, offset: number): number {
  let n = 0
  for (const word of line.words) {
    if (word.e <= offset) n += 1
    else break
  }
  return n
}

/** The remainder of the text from a mid-line seek, for the speech fallback. */
export function sliceFromOffset(line: LineAudio, offset: number): string {
  const idx = wordsBefore(line, offset)
  const word = line.words[idx]
  if (!word) return line.text
  return line.text.slice(word.ci)
}

async function defaultFetchBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`audio ${res.status}`)
  return await res.arrayBuffer()
}

/** Null-object bus port: the player boots and runs with no audio at all. */
export const SILENT_AUDIO: AudioBusPort = {
  unlocked: false,
  unlock: async () => false,
  playLine: (line) => noopHandle(line, 'silent'),
  stopLine: () => {},
  setMusic: () => {},
  playSfx: () => {},
  duck: () => {},
  setMuted: () => {},
  setVolume: () => {},
  setRate: () => {},
  setVoiceSpeed: () => {},
  preload: async () => {},
  evict: () => {},
  suspend: () => {},
  resume: () => {},
  acquireWakeLock: async () => false,
  releaseWakeLock: () => {},
  dispose: () => {},
}

/* ------------------------------------------------- the core AudioPort seam */

export interface AudioPortOptions {
  /** `Bundle.assets` — `sfx(uid)` / `music(uid)` are bundle asset ids, not URLs. */
  assets?: Record<string, { url?: string }> | null
  /** The bundle's own directory; every asset url resolves against it (§4.6.4). */
  baseUrl?: string | URL
  /** `StoryScript.audio.speed` — the band's TTS speed, used by the estimate. */
  speed?: number
  /** `CastEntry.voice` for a `say` cue's actor, so the fallback can match voices. */
  voiceFor?: (actor: string | null) => string | null | undefined
  /** `StoryScript.audio.music_gain_db` (−16 in today's compiler). */
  musicGainDb?: number
  /** Per-cue sfx gain when the cue carries none. */
  sfxGainDb?: number
}

/**
 * Wrap the bus in the `AudioPort` of `player/index.ts`, which is what
 * `runner.ts` holds.
 *
 * Two things the runner relies on, both honoured here:
 *
 * - `playLine` **never blocks**: it resolves as soon as playback has been
 *   arranged, whether that turned out to be a decoded buffer, a Web Speech
 *   utterance or silence.
 * - `currentLineTimeMs()` returns **−1 unless a decoded buffer is playing**,
 *   which is `nullAudioPort()`'s "no audio master" contract — the clock then
 *   steps itself and §8.1's determinism is untouched. Only a real stream is
 *   allowed to master the clock.
 */
export function createAudioPort(bus: AudioBusPort, opts: AudioPortOptions = {}): AudioPort {
  let onWord: ((index: number) => void) | null = null
  let current: LineHandle | null = null

  const urlOf = (uid: string | null | undefined): string | null => {
    if (!uid) return null
    const asset = opts.assets?.[uid]
    const raw = asset?.url ?? (uid.includes('/') && !uid.startsWith('audio:') ? uid : null)
    return raw ? resolveAssetUrl(raw, opts.baseUrl) : null
  }

  return {
    async unlock(): Promise<void> {
      await bus.unlock()
    },
    async playLine(cue: LineCue, whenMs: number): Promise<void> {
      const actor = (cue as { actor?: unknown }).actor
      const line = resolveLine(cue as LineCueLike, {
        baseUrl: opts.baseUrl,
        speed: opts.speed ?? 0.85,
        voice: opts.voiceFor?.(typeof actor === 'string' ? actor : null) ?? null,
      })
      current = bus.playLine(line, {
        offset: Math.max(0, whenMs) / 1000,
        onWord: (index) => onWord?.(index),
        onEnd: () => {
          current = null
        },
      })
    },
    stopLine(): void {
      current = null
      bus.stopLine()
    },
    /**
     * Decode the scene's baked voice lines up front.
     *
     * Without this the seam between the backend's baked Kokoro mp3s and the
     * player is broken in a way no unit test sees: `AudioBus.playLine` never
     * blocks, so a line whose bytes are not decoded yet falls straight through
     * to `startFallback` — Web Speech. The mp3 then warms in the background and
     * only a *replay* is heard in the baked voice. The child hears the robot
     * once per sentence, and because the fallback handle is `master: false` the
     * clock is never audio-mastered, so the native word timings the bake went
     * to such trouble to produce are never used either.
     *
     * The loader cannot do it: `AudioRef.url` is deliberately not a bundle
     * asset id (an id would hand an mp3 to pixi), so `sceneAssetIds` filters it
     * out. Voice bytes belong to the audio track, and this is where they load.
     */
    async preloadLines(cues: readonly LineCue[]): Promise<void> {
      const urls: string[] = []
      for (const cue of cues) {
        const raw = (cue as LineCueLike).audio
        const u = raw && typeof raw === 'object' && typeof raw.url === 'string' && raw.url.length > 0 ? raw.url : null
        if (u) urls.push(resolveAssetUrl(u, opts.baseUrl))
      }
      if (urls.length > 0) await bus.preload(urls)
    },
    duck(on: boolean): void {
      bus.duck(on)
    },
    async preloadSounds(uids: readonly string[]): Promise<void> {
      await bus.preload([...new Set(uids)].map(urlOf).filter((url): url is string => !!url))
    },
    stopSounds(): void { bus.stopSfx?.() },
    pauseBackground(paused: boolean): void { bus.pauseBackground?.(paused) },
    music(uid: string | null, gainDb?: number, fade?: number): void {
      bus.setMusic(urlOf(uid), { gainDb: gainDb ?? opts.musicGainDb ?? -20, fade })
    },
    sfx(uid: string, gainDb?: number, duration?: number): void {
      // A uid the bundle does not carry is silence, by design (ASSET-FACTS).
      bus.playSfx(urlOf(uid), { gainDb: gainDb ?? opts.sfxGainDb ?? -14, duration })
    },
    onWord(cb: (wordIndex: number) => void): void {
      onWord = cb
    },
    currentLineTimeMs(): number {
      return current && current.master ? current.elapsed() * 1000 : -1
    },
    setRate(rate: number): void {
      bus.setRate(rate)
    },
    setVoiceSpeed(rate: number): void {
      bus.setRate(rate)
    },
  }
}
